import { useEffect, useState } from 'react'
import moment from 'moment';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { ReactComponent as PlanIcon }  from '../../../icons/PlanIcon.svg'
import { ReactComponent as RequirementsIcon }  from '../../../icons/RequirementsIcon.svg'
import { ReactComponent as LocationIcon }  from '../../../icons/locationIcon.svg'
import { ReactComponent as HeartIcon } from '../../../icons/HeartIcon.svg';
import { ReactComponent as HeartOutlineIcon } from '../../../icons/HeartOutlineIcon.svg';
import { ReactComponent as BookingsIcon } from '../../../icons/Bookings.svg';
// import { ReactComponent as MenuIcon } from '../../../icons/MenuIcon.svg';
import { ReactComponent as ChatOutlineIcon } from '../../../icons/ChatOutlineIcon.svg';
import { ReactComponent as WebsiteIcon } from '../../../icons/WebsiteIcon.svg';
import { ReactComponent as ClockIconFilled } from '../../../icons/ClockIconFilled.svg';
import { ReactComponent as BookingPlanIcon }  from '../../../icons/BookingPlanIcon.svg'
import { ReactComponent as MembershipTiersIcon }  from '../../../icons/communityIcon.svg'
import { ReactComponent as InventoryIcon }  from '../../../icons/InventoryIcon.svg'
import { ReactComponent as ReviewsIcon }  from '../../../icons/StarIcon.svg'
import { ReactComponent as RulesIcon }  from '../../../icons/RulesIcon.svg'
import { ReactComponent as SchedulesIcon }  from '../../../icons/SchedulesIcon.svg'
import { ReactComponent as NavResourcesIconDark }  from '../../../icons/NavResourcesIconDark.svg'

import { ReactComponent as DescriptionIcon } from '../../../icons/DescriptionIcon.svg';
import { ReactComponent as PostShareIcon } from '../../../icons/PostShare.svg';
import {  ResourceSchema, ResourceType, SearchFilterOptionType, ResourceBookingSchema, ResourceOptionType, ResourcePlanSchema, RequirementSchema, ResourceInventorySchema, InventorySchema, TrainingOrganizationSchema, TrainingSchema, MembershipSchema, TimeSlot, CurrentRequirementsSchema, CredentialSchema, AddRequirementStatus, ToggleApplyModalOptions, PlanSchema } from '../../../data/types'
import { useLoadCurrentResource, useLoadMembershipPlans, useLoadResourceOptions } from '../../../hooks/apphooks'
import { useAddBooking, useAddMultipleBookings, useCompleteSignIn, useLoadMember, useLoadResourceBookings, useUpdateAccountDetails, useGetMemberRequirementsStatus, useAddCredential } from '../../../hooks/authhooks';
// import StarRating from '../../../components/Mobile/StarRating'
import NotFound from '../notfound';
import { PhotoPreviewModal, RequestMembershipModal, ApplyFundingModal, RegisterTrainingModal, ObtainDocumentationModal, RequestKitchenModal, OrderInventoryModal, ScheduleExpertModal, MorePlanDetailsModal, AddCredentialModal } from '../../../molecules/Mobile/AppModal'
import ResourceDetailsSkeleton from '../../../components/ResourceDetailsSkeleton';
import { ResourceInventoryGrid } from '../../../components/ResourceInventoryGrid';

import { getPeriodStyles, capitalizeString, isResourceSaved, getDirectLinkFromGoogleDriveSharedLink, getActionButtonLabel, getMembershipStatusColor, styleThousandCommas, planRecurrenceMap, getMemberBookingPlanIdForResource, getMemberBookingBalanceForResource, buildRequirementStatusDisplay, isRequirementEffectivelyMet } from '../../../utils/helpers';
import { CheckBoxOutlineBlank, CheckBox, Error, ReportProblem, CheckCircle } from '@mui/icons-material';
// import NewSearch from '../../components/NewSearch';


type RouteParams = { 
  resourceType: ResourceType
  resourceId: string
};


type ResourceDetailsProps = {
  searchFilterOptions: SearchFilterOptionType
}

type PhotoGalleryProps = {
  photos: string[]
  resourceName: string
  onPhotoClick: (index: number) => void
  resourceIcon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>
}

// Helper function to get photos with backwards compatibility for bgPhoto
const getResourcePhotos = (resource: ResourceSchema | undefined): string[] => {
  if (!resource) return []
  
  // If bgPhotos exists and has items, use it
  if (resource.bgPhotos && resource.bgPhotos.length > 0) {
    return resource.bgPhotos
  }
  
  // Fallback to bgPhoto (singular) for backwards compatibility
  if (resource.bgPhoto) {
    return [resource.bgPhoto]
  }
  
  return []
}

const PhotoGallery: React.FC<PhotoGalleryProps> = ({ photos, resourceName, onPhotoClick, resourceIcon: ResourceIcon }) => {
  const [imageErrors, setImageErrors] = useState<Set<number>>(new Set())
  
  // Responsive height with aspect ratio constraints
  // Using fixed heights with max constraints to prevent overlap with menu list below
  const galleryHeight = 'h-[240px] sm:h-[280px] md:h-[320px] lg:h-[400px] xl:h-[450px] max-h-[40vh]'
  const gallerySpacing = 'my-2'

  const getPhotoUrl = (photo: string) => {
    return getDirectLinkFromGoogleDriveSharedLink(photo) || ''
  }

  const handleImageError = (index: number) => {
    setImageErrors(prev => new Set(prev).add(index))
  }

  // Default empty state with improved design
  const renderEmptyState = () => (
    <div className={`${galleryHeight} w-full ${gallerySpacing} rounded-md bg-gradient-to-br from-gray-300 via-gray-200 to-gray-400 flex items-center justify-center`}>
      <div className="text-center p-4">
        {ResourceIcon && (
          <ResourceIcon className="w-20 h-20 sm:w-24 sm:h-24 text-gray-400 mx-auto mb-3 opacity-60" />
        )}
        <p className="text-sm sm:text-base text-gray-500 font-raleway font-medium">
          {resourceName || 'Resource'}
        </p>
      </div>
    </div>
  )

  if (!photos || photos.length === 0) {
    return renderEmptyState()
  }

  const validPhotos = photos.filter(p => getPhotoUrl(p))
  
  if (validPhotos.length === 0) {
    return renderEmptyState()
  }

  // Helper to render image with error handling
  const renderImage = (photo: string, index: number, alt: string, className: string = 'object-cover w-full h-full min-h-0', iconSize: string = 'w-12 h-12') => {
    if (imageErrors.has(index)) {
      return (
        <div className="w-full h-full bg-gradient-to-br from-gray-300 via-gray-200 to-gray-400 flex items-center justify-center">
          {ResourceIcon && <ResourceIcon className={`${iconSize} text-gray-400 opacity-60`} />}
        </div>
      )
    }
    return (
      <img 
        className={`${className} transition-opacity duration-300`}
        src={getPhotoUrl(photo)} 
        alt={alt}
        onError={() => handleImageError(index)}
      />
    )
  }

  // Single photo - full width
  if (validPhotos.length === 1) {
    return (
      <div className={`w-full ${galleryHeight} ${gallerySpacing} relative z-0 overflow-hidden`}>
        {imageErrors.has(0) ? (
          renderEmptyState()
        ) : (
          <img 
            className='object-cover w-full h-full rounded-md cursor-pointer transition-opacity duration-300' 
            src={getPhotoUrl(validPhotos[0])} 
            alt={resourceName}
            onClick={() => onPhotoClick(0)}
            onError={() => handleImageError(0)}
          />
        )}
      </div>
    )
  }

  // 2 photos - large left, 1 smaller on right
  if (validPhotos.length === 2) {
    const mainPhoto = validPhotos[0]
    const sidePhoto = validPhotos[1]

    return (
      <div className={`w-full ${galleryHeight} ${gallerySpacing} grid grid-cols-[2fr_1fr] gap-2 relative z-0`}>
        {/* Main large photo on left */}
        <div className='relative overflow-hidden rounded-md cursor-pointer min-h-0 min-w-0' onClick={() => onPhotoClick(0)}>
          {renderImage(mainPhoto, 0, `${resourceName} 1`)}
        </div>
        
        {/* Smaller photo on right */}
        <div className='relative overflow-hidden rounded-md cursor-pointer min-h-0 min-w-0' onClick={() => onPhotoClick(1)}>
          {renderImage(sidePhoto, 1, `${resourceName} 2`)}
        </div>
      </div>
    )
  }

  // 3 photos - large left, 2 smaller on right
  if (validPhotos.length === 3) {
    const mainPhoto = validPhotos[0]
    const others = validPhotos.slice(1)

    return (
      <div className={`w-full ${galleryHeight} ${gallerySpacing} grid grid-cols-3 grid-rows-2 gap-2 relative z-0`}>
        {/* Main */}
        <div
          className='relative overflow-hidden rounded-md cursor-pointer col-span-2 row-span-2 min-h-0 min-w-0'
          onClick={() => onPhotoClick(0)}
        >
          {renderImage(mainPhoto, 0, `${resourceName} 1`)}
        </div>

        {/* Smaller */}
        {others.map((photo, idx) => (
          <div
            key={idx}
            className='relative overflow-hidden rounded-md cursor-pointer min-h-0 min-w-0'
            onClick={() => onPhotoClick(idx + 1)}
          >
            {renderImage(photo, idx + 1, `${resourceName} ${idx + 2}`)}
          </div>
        ))}
        {/* Empty placeholder for 3rd slot */}
        <div className='bg-gradient-to-br from-gray-300 via-gray-200 to-gray-400 rounded-md min-h-0 min-w-0'></div>
      </div>
    )
  }

  // 4 photos - large left, 3 smaller on right, with "View all" overlay on last
  if (validPhotos.length === 4) {
    const mainPhoto = validPhotos[0]
    const sidePhotos = validPhotos.slice(1, 4)

    return (
      <div className={`w-full ${galleryHeight} ${gallerySpacing} grid grid-cols-[2fr_1fr] gap-2 relative z-0`}>
        {/* Main large photo on left */}
        <div className='relative overflow-hidden rounded-md cursor-pointer min-h-0 min-w-0' onClick={() => onPhotoClick(0)}>
          {renderImage(mainPhoto, 0, `${resourceName} 1`)}
        </div>
        
        {/* Smaller photos on right */}
        <div className='grid grid-rows-3 gap-2 min-h-0 min-w-0'>
          {sidePhotos.map((photo, index) => (
            <div 
              key={index + 1} 
              className='relative overflow-hidden rounded-md cursor-pointer min-h-0 min-w-0' 
              onClick={() => onPhotoClick(index + 1)}
            >
              {renderImage(photo, index + 1, `${resourceName} ${index + 2}`)}
              {index === 2 && (
                <div 
                  className='absolute inset-0 bg-[#013847]/70 flex items-center justify-center cursor-pointer hover:bg-[#013847]/80 transition-colors'
                  onClick={(e) => {
                    e.stopPropagation()
                    onPhotoClick(0)
                  }}
                >
                  <div className='flex flex-col items-center text-white'>
                    <NavResourcesIconDark className='mb-2' style={{ fontSize: 32 }} />
                    <span className='text-[16px] font-bold'>View all (4)</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    )
  }

  // 5+ photos - large left, 3 smaller on right, with "View all" overlay on last
  const mainPhoto = validPhotos[0]
  const sidePhotos = validPhotos.slice(1, 4)

  return (
    <div className={`w-full ${galleryHeight} ${gallerySpacing} grid grid-cols-[2fr_1fr] gap-2 relative z-0`}>
      {/* Main large photo on left */}
      <div className='relative overflow-hidden rounded-md cursor-pointer min-h-0 min-w-0' onClick={() => onPhotoClick(0)}>
        {renderImage(mainPhoto, 0, `${resourceName} 1`)}
      </div>
      
      {/* Smaller photos on right */}
      <div className='grid grid-rows-3 gap-2 min-h-0 min-w-0'>
        {Array.from({ length: 3 }).map((_, index) => {
          const photo = sidePhotos[index]
          if (!photo) {
            return <div key={index + 1} className='bg-gradient-to-br from-gray-300 via-gray-200 to-gray-400 rounded-md min-h-0 min-w-0'></div>
          }
          
          return (
            <div 
              key={index + 1} 
              className='relative overflow-hidden rounded-md cursor-pointer min-h-0 min-w-0' 
              onClick={() => onPhotoClick(index + 1)}
            >
              {renderImage(photo, index + 1, `${resourceName} ${index + 2}`)}
              {index === 2 && (
                <div 
                  className='absolute inset-0 bg-[#013847]/70 flex items-center justify-center cursor-pointer hover:bg-[#013847]/80 transition-colors'
                  onClick={(e) => {
                    e.stopPropagation()
                    onPhotoClick(0)
                  }}
                >
                  <div className='flex flex-col items-center text-white'>
                    <NavResourcesIconDark className='mb-2' style={{ fontSize: 32 }} />
                    <span className='text-[16px] font-bold'>View all ({validPhotos.length})</span>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}


const DETAILS_MENU_NAMES = [
  "description",
  "plans",
  "tiers",
  "requirements",
  "hours",
  "inventory",
  "reviews",
  "schedules",
  "amount",
  "rules",
] as const

type detailsMenuNames = (typeof DETAILS_MENU_NAMES)[number] | ""

/** Maps ?tab= to a details accordion section; remaps tiers → plans for active members */
const resolveDetailsMenuFromTabQuery = (
  tabQuery: string | null,
  hasActiveMembership: boolean
): detailsMenuNames => {
  if (!tabQuery || !(DETAILS_MENU_NAMES as readonly string[]).includes(tabQuery)) {
    return "description"
  }
  if (tabQuery === "tiers" && hasActiveMembership) {
    return "plans"
  }
  return tabQuery as detailsMenuNames
}


const ResourceDetails: React.FC<ResourceDetailsProps> = ({searchFilterOptions}) => {
  var navigate = useNavigate();
  
  const { resourceType, resourceId } = useParams<RouteParams>()
  const [searchParams] = useSearchParams();
  const homeQuery = searchParams.get('home');
  const menuTabQuery = searchParams.get('tab');

  const { setSignInModalIsOpen, setMessagesOpen, setMessageCommunityId } = searchFilterOptions

  const { currentResource, isLoadingCurrentResource } = useLoadCurrentResource(resourceId ? resourceId : "")
  const { user } = useCompleteSignIn()
  const loggedInUsername = (user && user?.username) ? user.username : ''
  const loggedInUid = (user && user?.uid) ? user.uid : ''
  const loggedInEmail = (user && user?.email) ? user.email : ''
  const userAvatar = (user && user?.avatar) ? user.avatar : ''

  const [applyModal, toggleApplyModal] = useState<ResourceType | undefined>()
  const [inventoryOnlyItem, setInventoryOnlyItem] = useState<InventorySchema | undefined>()

  const handleToggleApplyModal = (resourceType: ResourceType, options?: ToggleApplyModalOptions) => {
    if (resourceType === 'kitchen' && options?.inventoryItem) {
      setInventoryOnlyItem(options.inventoryItem)
    } else {
      setInventoryOnlyItem(undefined)
    }
    toggleApplyModal(resourceType)
  }

  const closeApplyModal = () => {
    toggleApplyModal(undefined)
    setInventoryOnlyItem(undefined)
  }
  const [membershipModal, setMembershipModal] = useState<null | "request" | "manage">(null)
  const [selectedMembershipPlan, setSelectedMembershipPlan] = useState<PlanSchema | null>(null)

  /** Opens/closes the membership modal and optionally pre-selects a membership tier */
  const toggleMembershipModal = (formType: "request" | "manage" | null, plan?: PlanSchema) => {
    setSelectedMembershipPlan(plan ?? null)
    setMembershipModal(formType)
  }
  const [addingCredential, toggleAddingCredential] = useState<AddRequirementStatus>({ status: false })

  useEffect(() => {
    document.title = `${capitalizeString(resourceType ? resourceType : '')} ${currentResource?.name ? `| ${currentResource?.name}` : ''}`
    // eslint-disable-next-line 
  }, [loggedInUid, loggedInUsername]);

  const {getResourceIcon} = useLoadResourceOptions()
  const ResourceIcon = getResourceIcon(resourceType)

  const openInNewTab = (url: string) => {
    if (url){
      window.open(url, "_blank");
    }
  };

  const { saveResource, removeResource } = useUpdateAccountDetails(loggedInUid)

  const handleSaveResource = () => {
    if (resourceId){
      if (!loggedInUid){
        setSignInModalIsOpen(true)
      } else {
        saveResource(resourceId)
      }
    }
  }

  const handleRemoveSavedResource = () => {
    if (resourceId){
      removeResource(resourceId)
    }
  }

  const handleShare = async () => {
    const url = window.location.href
    const title = currentResource?.name || 'Resource'
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          text: `Check out this ${resourceType}: ${title}`,
          url: url,
        })
      } catch (err) {
        // User cancelled or error occurred
        console.log('Error sharing:', err)
      }
    } else {
      // Fallback: Copy to clipboard
      try {
        await navigator.clipboard.writeText(url)
        // You could show a toast notification here
        alert('Link copied to clipboard!')
      } catch (err) {
        console.log('Error copying to clipboard:', err)
      }
    }
  }

  // const resourceIsSaved = true
  const savedResources = user?.saved_resources ? user?.saved_resources : []
  var resourceIsSaved: any  = false
  if (resourceId){
     resourceIsSaved = isResourceSaved<ResourceSchema>(resourceId, savedResources)
  }
  

  var actionButtonLabel = getActionButtonLabel(resourceType as ResourceType)

  // const [width, setWidth] = useState(window.innerWidth);

  // function handleWindowSizeChange() {
  //   setWidth(window.innerWidth);
  // }

  const communityId = currentResource?.community_id ? currentResource?.community_id : ""
  const { currentMember } = useLoadMember(communityId)
  const { resourceBookings } = useLoadResourceBookings(communityId, currentResource?.id || "")
  const { addMemberCredential } = useAddCredential(loggedInUid)

  useEffect(() => {
    // document.title = `HomeCook | ${currentResource?.resource_type ? capitalizeString(currentResource?.resource_type) : 'Resource'} Listing`
    // window.addEventListener("resize", handleWindowSizeChange);
    // return () => {
    //   window.removeEventListener("resize", handleWindowSizeChange);
    // };
  }, [currentResource?.resource_type, currentMember]);

  const { addNewBooking} = useAddBooking(loggedInUid)
  const { addMultipleBookings } = useAddMultipleBookings(loggedInUid)

  const applyBookingResourceFields = (
    resource: ResourceSchema,
    resourceType: ResourceType,
    booking: ResourceBookingSchema
  ) => {
    booking.resource_id = resource.id
    booking.resource_type = resourceType
    booking.username = loggedInUsername
    booking.user_email = loggedInEmail
    booking.user_full_name = `${user?.first_name} ${user?.last_name}`.trim()
    booking.user_avatar = userAvatar
    booking.resource_name = resource.name
    booking.resource_city = resource.city
    booking.resource_county = resource.county
    booking.resource_state = resource.state
    booking.resource_address = resource.address
    booking.resource_logo = resource.logo
    const resourcePhotos = getResourcePhotos(resource)
    booking.resource_bgPhoto = resourcePhotos.length > 0 ? resourcePhotos[0] : ''
    booking.community_id = resource.community_id
    booking.timezone = resource.timezone
  }

  const handleSubmitCredential = (credential: CredentialSchema, communityId: string) => {
    if (loggedInUsername && credential) {
      credential.username = loggedInUsername;
      addMemberCredential(communityId, credential);
    }
  }

  const handleSubmitRequest = async (resource: ResourceSchema, resourceType: ResourceType, booking: ResourceBookingSchema) => {
    if (loggedInUsername && loggedInEmail && resourceType && resource.id){
      applyBookingResourceFields(resource, resourceType, booking)
      const isSuccess = await addNewBooking(booking)
      if(isSuccess){
        navigate(`/bookings`)
      }
      return isSuccess ? true : false
    }
    return false
  }

  const handleSubmitMultipleRequest = async (
    resource: ResourceSchema,
    resourceType: ResourceType,
    bookings: ResourceBookingSchema[]
  ) => {
    if (loggedInUsername && loggedInEmail && resourceType && resource.id && bookings.length > 0) {
      bookings.forEach((booking) => applyBookingResourceFields(resource, resourceType, booking))
      const isSuccess = await addMultipleBookings({ resource_id: resource.id, bookings })
      if (isSuccess) {
        navigate(`/bookings`)
      }
      return isSuccess ? true : false
    }
    return false
  }

  const currentResourceOptions = currentResource?.resource_options as ResourceOptionType

  const inventoryCatalogItems = (currentResource?.resource_inventory ?? {}) as ResourceInventorySchema
  const catalogOptions: string[] = Object.keys(inventoryCatalogItems)
  const catalogOption: string = catalogOptions ? catalogOptions[0] : ""
  const availableItems = inventoryCatalogItems[catalogOption]


  const trainingOrganizations = (currentResource?.resource_inventory ?? {}) as TrainingOrganizationSchema
  const organizations: string[] = Object.keys(trainingOrganizations)
  const trainingOrganization: string = organizations ? organizations[0] : ""
  const availableDates = trainingOrganizations[trainingOrganization]

  if (availableDates){
    availableDates.sort((a, b) => {
      const dateA = moment(a.date);
      const dateB = moment(b.date);
      return dateA.diff(dateB);
    });
  }


  type resourceDetailsMenuType = {
    name: detailsMenuNames
    label: string
  }
  var resourceDetailsMenu: resourceDetailsMenuType[] = [
    {
      name: "description",
      label: "Description"
    }
  ]

  if (currentResource?.resource_type && ["kitchen", "farm"].includes(currentResource?.resource_type)){

    resourceDetailsMenu = [
      ...resourceDetailsMenu,
      {
        name: "hours",
        label: "Operating Hours"
      },
      // {
      //   name: "inventory",
      //   label: "Inventory"
      // }
    ]

    if (currentResource?.resource_type === "kitchen"){
      if (currentMember && currentMember?.status){
        resourceDetailsMenu.push(
          {
            name: "plans",
            label: "Booking Plans"
          },
          {
            name: "requirements",
            label: "Requirements"
          },
          {
            name: "rules",
            label: "Kitchen Rules"
          }
        )
      } else {
        resourceDetailsMenu.push(
          {
            name: "tiers",
            label: "Membership Tiers"
          },
          {
            name: "plans",
            label: "Booking Plans"
          },
          {
            name: "requirements",
            label: "Requirements"
          },
          {
            name: "rules",
            label: "Kitchen Rules"
          }
        )
      }
    }

    resourceDetailsMenu = [
      ...resourceDetailsMenu,
      {
        name: "inventory",
        label: "Inventory"
      },
      {
        name: "reviews",
        label: "Reviews"
      }
    ]
    // resourceDetailsMenu.push(
    //   {
    //     name: "reviews",
    //     label: "Reviews"
    //   }
    // )

  } else if (currentResource?.resource_type && ["training", "expert"].includes(currentResource?.resource_type)){
    resourceDetailsMenu = [
      ...resourceDetailsMenu,
      {
        name: "schedules",
        label: "Schedules"
      },
      {
        name: "reviews",
        label: "Reviews"
      }
    ]
  }

  const hasActiveMembership = !!(currentMember && currentMember?.status)
  const [currentDetailsMenu, setcurrentDetailsMenu] = useState<detailsMenuNames>(
    resolveDetailsMenuFromTabQuery(menuTabQuery, hasActiveMembership)
  )
  useEffect(() => {
    if (menuTabQuery !== null) {
      setcurrentDetailsMenu(resolveDetailsMenuFromTabQuery(menuTabQuery, hasActiveMembership))
    }
  }, [menuTabQuery, hasActiveMembership])
  const [isMenuDropdownOpen, setIsMenuDropdownOpen] = useState<boolean>(false)

  const [selectedPlan, setSelectedPlan] = useState<ResourcePlanSchema>({} as ResourcePlanSchema)
  const [photoModalOpen, setPhotoModalOpen] = useState<boolean>(false)
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number>(0)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isMenuDropdownOpen) {
        const target = event.target as Element;
        if (!target.closest('.menu-dropdown-container')) {
          setIsMenuDropdownOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuDropdownOpen]);

  // console.log("actionButtonLabel", actionButtonLabel)

  // const isMobile = width < 1024;

  // Show loading skeleton while resource is being fetched
  if (isLoadingCurrentResource) {
    return <ResourceDetailsSkeleton />
  }

  if (!currentResource){
    return <NotFound dataName="resource"  />
  } else if (currentResource && !currentResource?.name){
    return <ResourceDetailsSkeleton />
  }
  // else if (isMobile && currentResource){
  //   return (
  //     <>
  //       <div className={`flex mx-auto w-full p-4 h-${currentResource && currentResource.description ? `[${currentResource.description.length * 3.7}px]` : 'screen'} bg-[#FFFFFF] font-work-sans`}>
  //           <div className='font-raleway flex flex-col w-full relative items-start mt-[180px] '>
  //               <>
  //                   {
  //                     (homeQuery || !resourceType) ? (
  //                       <></>
  //                     ) : (
  //                         <span className="text-[#8A8A8A] hover:text-[#5DB6CE] text-[13px] font-bold mb-4 cursor-pointer" onClick={() => navigate(-1)}><ArrowBackIosIcon style={{fontSize: 13}}  />Back</span>
  //                     )
  //                   }
  //                   <div className='flex flex-col w-11/12 mx-auto'>
  //                     <div className='flex flex-row'>
  //                       {
  //                           getDirectLinkFromGoogleDriveSharedLink(currentResource?.logo) ?
  //                           ( 
  //                           <div className='p-2 border-[#E9E9E9] w-16 border-[1px] rounded-md'>
  //                             <img className='h-10 aspect-auto object-contain' alt={getDirectLinkFromGoogleDriveSharedLink(currentResource.logo)} src={getDirectLinkFromGoogleDriveSharedLink(currentResource.logo)} />
  //                           </div>
  //                           ) : (
  //                             <div className='p-2 border-[#E9E9E9] border-[1px] rounded-md'>
  //                               <ResourceIcon className={'relative m-1 mx-auto w-10 aspect-square p-1 text-[#5DB6CE]/60 '} />
  //                             </div>
  //                           )
  //                       }

  //                       <div className='flex flex-row my-auto text-[14px] font-semibold ml-auto '>
  //                         {
  //                           (!resourceIsSaved)? (
  //                             <div className='flex flex-row text-black px-3 cursor-pointer border-[#E9E9E9] border-[1px] p-2 rounded-lg' onClick={handleSaveResource}>
  //                               <HeartOutlineIcon className='text-black w-[16px] h-[15px] my-auto' />
  //                               <p className='mx-2 my-auto'>Save</p>
  //                             </div>
  //                           ) : (
  //                             <div className='flex flex-row text-black px-3 cursor-pointer border-[#E9E9E9] border-[1px] p-2 rounded-lg' onClick={handleRemoveSavedResource}>
  //                               <HeartIcon className='text-black w-[16px] h-[15px] my-auto' />
  //                               <p className='mx-2 my-auto'>Saved</p>
  //                             </div>
  //                           )
  //                         }
  //                       </div>
  //                     </div>

  //                     <div className='flex flex-col w-full'> 
  //                       <span className='flex flex-row mx-2 sm:text-left text-center mt-2 sm:mt-auto my-auto font-semibold'>{currentResource ? `${currentResource.name}` : null}
  //                         {
  //                           (currentMember && currentMember.status) ? (
  //                             <span 
  //                               className="font-raleway text-center font-medium text-xs ml-2 px-2 py-1 rounded-md my-auto"
  //                               style={{
  //                                 backgroundColor: getMembershipStatusColor(currentMember.status).bgColor,
  //                                 color: getMembershipStatusColor(currentMember.status).primaryTextColor
  //                               }}
  //                             >
  //                               {capitalizeString(currentMember.status)}
  //                             </span>
  //                           ) : (<></>)
  //                         }
  //                       </span>
  //                       {
  //                         currentResource?.rating ? (
  //                           <div className='flex flex-col my-1 sm:mx-4 mx-auto items-center w-full sm:w-auto'>
  //                             <li
  //                               className="grid grid-flow-col justify-start text-primaryDark-200 text-sm font-open-sans"
  //                             >
  //                               <div className='flex flex-row flex-start mx-2 mt-1 mb-2'>
  //                                 <StarRating review_rating={currentResource.rating} /> &nbsp; (1)
  //                               </div>
  //                             </li>
  //                           </div> ) : (<></>)
  //                       }
                        
  //                       {/* <span className='font-raleway w-full flex flex-col sm:mx-1 mx-auto p-1 items-center'> */}
  //                       <div className='flex flex-row text-left text-[#8A8A8A] text-[14px]'>
  //                         <LocationIcon className='w-[10px] h-[14px] m-1 text-[#8A8A8A]' /> &nbsp;
  //                         {currentResource && currentResource.city ? `${currentResource.city}, `: ``} 
  //                         {currentResource && currentResource.county ? `${currentResource.county}, `: ``} 
  //                         {currentResource && currentResource.state ? `${currentResource.state} `: ``}  
  //                         {currentResource && currentResource.zipcode ? `${currentResource.zipcode} `: ``}  
  //                         {
  //                           (
  //                             !currentResource.city && !currentResource.county && !currentResource.state && !currentResource.zipcode
  //                           ) ? 'Nationwide' : ''
  //                         }
  //                       </div>
  //                       {/* </span> */}
  //                     </div>
  //                   </div>
                      

  //                   <div className='w-11/12 mx-auto my-2'>
  //                     <PhotoGallery
  //                       photos={getResourcePhotos(currentResource)}
  //                       resourceName={currentResource?.name || ''}
  //                       resourceIcon={ResourceIcon}
  //                       onPhotoClick={(index) => {
  //                         setSelectedPhotoIndex(index)
  //                         setPhotoModalOpen(true)
  //                       }}
  //                     />
  //                   </div>
                    
  //                   <div className='flex flex-col w-full m-1'>
  //                       <div className='col-span-2 w-full'>
  //                           <div className='flex flex-col mt-4 font-raleway w-11/12 mx-auto'>
  //                               <p className='font-semibold text-[#000000]  text-[16px] bg-[#F3F3F3] w-[110px] border-[#E9E9E9] border-[1px] rounded-lg p-2 mb-4'>Description</p>
  //                               <div className='text-[#000] text-[16px] border-[#E9E9E9] border-[1px] rounded-lg p-6'>
  //                                     {
  //                                       (currentResource && currentResource.description) ? (
  //                                         <p>{currentResource.description}</p>
  //                                       ) : (<></>)
  //                                     }

  //                                   </div>

  //                                   <button className='flex rounded-md flex-row my-2 justify-center mt-8 border-[#E9E9E9] border-[1px] px-3 text-[#013847] font-bold cursor-pointer h-[40px] text-center' onClick={() => openInNewTab(currentResource?.website ? currentResource.website : '')}>Visit Website</button>

  //                                   {
  //                                     (currentResource && currentResource.active && actionButtonLabel && actionButtonLabel !== "Book") ? (
  //                                       <button 
  //                                         onClick={()=> {
  //                                           if (!loggedInUid){
  //                                               setSignInModalIsOpen(true)
  //                                           }
  //                                           toggleApplyModal(resourceType)

  //                                         }} 
  //                                         className='bg-primaryDark-0 text-white text-[16px] font-extrabold h-[40px] rounded-md  border-0'>
  //                                           {actionButtonLabel}
  //                                       </button>
  //                                     ) : (
  //                                       <></>
  //                                     )
  //                                   }
  //                           </div>
  //                           {/* {
  //                             (currentResource.resource_type === "kitchen") ? (
  //                               <></>
  //                             ) : (
  //                               <></>
  //                             )
  //                           } */}
  //                       </div>
  //                   </div>
  //               </>
  //           </div>
  //       </div>

  //       <RequestKitchenModal selectedPlan={selectedPlan || {} as ResourcePlanSchema} isOpen={!!loggedInUid && applyModal==="kitchen"} resourceDetail={currentResource} handleSubmitRequest={handleSubmitRequest} closeAppModal={()=>toggleApplyModal(undefined)} />
  //       <RegisterTrainingModal isOpen={!!loggedInUid && applyModal==="training"} resourceDetail={currentResource} handleSubmitRequest={handleSubmitRequest} closeAppModal={()=>toggleApplyModal(undefined)} />
  //       <ObtainDocumentationModal isOpen={!!loggedInUid && applyModal==="documentation"} resourceDetail={currentResource} handleSubmitRequest={handleSubmitRequest} closeAppModal={()=>toggleApplyModal(undefined)} />
  //       <ApplyFundingModal isOpen={!!loggedInUid && applyModal==="funding"} resourceDetail={currentResource} handleSubmitRequest={handleSubmitRequest} closeAppModal={()=>toggleApplyModal(undefined)} />
  //       <ScheduleExpertModal isOpen={!!loggedInUid && applyModal==="expert"} resourceDetail={currentResource} handleSubmitRequest={handleSubmitRequest} closeAppModal={()=>toggleApplyModal(undefined)} />
  //       <OrderInventoryModal isOpen={!!loggedInUid && applyModal==="farm"} resourceDetail={currentResource} handleSubmitRequest={handleSubmitRequest} closeAppModal={()=>toggleApplyModal(undefined)}  />
  //       <PhotoPreviewModal
  //         isOpen={photoModalOpen}
  //         photos={getResourcePhotos(currentResource)}
  //         currentIndex={selectedPhotoIndex}
  //         resourceName={currentResource?.name || ''}
  //         onClose={() => setPhotoModalOpen(false)}
  //         onNavigate={(index: number) => setSelectedPhotoIndex(index)}
  //       />
  //     </>
  //   )
  // }
  else {
    return (
      <>
        <div className={`flex mx-auto w-full p-4 h-${currentResource && currentResource.description ? `[${currentResource.description.length * 3.7}px]` : 'screen'} bg-[#FFFFFF] font-work-sans`}>
            <div className='font-raleway flex flex-col w-full relative items-start '>
                <>
                    {
                      (homeQuery || !resourceType) ? (
                        <></>
                      ) : (
                          <span className="text-[#8A8A8A] hover:text-[#5DB6CE] text-[13px] font-bold mb-4 cursor-pointer" onClick={() => navigate(-1)}><ArrowBackIosIcon style={{fontSize: 13}}  />Back</span>
                      )
                    }
                    <div className='flex flex-row w-11/12 mx-auto'>
                      <div className='flex flex-row'>
                        {
                            getDirectLinkFromGoogleDriveSharedLink(currentResource?.logo) ?
                            ( 
                            <div className='p-2 border-[#E9E9E9] w-16 border-[1px] rounded-md'>
                              <img className='h-10 aspect-auto object-contain' alt={getDirectLinkFromGoogleDriveSharedLink(currentResource.logo)} src={getDirectLinkFromGoogleDriveSharedLink(currentResource.logo)} />
                            </div>
                            ) : (
                              <div className='p-2 border-[#E9E9E9] border-[1px] rounded-md'>
                                <ResourceIcon className={'relative m-1 mx-auto w-10 aspect-square p-1 text-[#5DB6CE]/60 '} />
                              </div>
                            )
                        }

                        {/* <div className='flex flex-row my-auto text-[14px] font-semibold ml-auto '>
                          {
                            (!resourceIsSaved)? (
                              <div className='flex flex-row text-black px-3 cursor-pointer border-[#E9E9E9] border-[1px] p-2 rounded-lg' onClick={handleSaveResource}>
                                <HeartOutlineIcon className='text-black w-[16px] h-[15px] my-auto' />
                                <p className='mx-2 my-auto'>Save</p>
                              </div>
                            ) : (
                              <div className='flex flex-row text-black px-3 cursor-pointer border-[#E9E9E9] border-[1px] p-2 rounded-lg' onClick={handleRemoveSavedResource}>
                                <HeartIcon className='text-black w-[16px] h-[15px] my-auto' />
                                <p className='mx-2 my-auto'>Saved</p>
                              </div>
                            )
                          }
                        </div> */}
                      </div>

                      <div className='flex flex-col w-full'> 
                        <span className='flex flex-row mx-3 sm:text-left font-raleway text-center mt-2 sm:mt-auto my-auto font-semibold'>{currentResource ? `${currentResource.name}` : null}
                          {
                            (currentMember && currentMember.status) ? (
                              <span 
                                className="font-raleway text-center font-medium text-xs ml-2 px-2 py-1 rounded-md my-auto"
                                style={{
                                  backgroundColor: getMembershipStatusColor(currentMember.status).bgColor,
                                  color: getMembershipStatusColor(currentMember.status).primaryTextColor
                                }}
                              >
                                {capitalizeString(currentMember.status)}
                              </span>
                            ) : (<></>)
                          }
                        </span>
                        {/* {
                          currentResource?.rating ? (
                            <div className='flex flex-col my-1 sm:mx-4 mx-auto items-center w-full sm:w-auto'>
                              <li
                                className="grid grid-flow-col justify-start text-primaryDark-200 text-sm font-open-sans"
                              >
                                <div className='flex flex-row flex-start mx-2 mt-1 mb-2'>
                                  <StarRating review_rating={currentResource.rating} /> &nbsp; (1)
                                </div>
                              </li>
                            </div> ) : (<></>)
                        } */}
                        
                        {/* <span className='font-raleway w-full flex flex-col sm:mx-1 mx-auto p-1 items-center'> */}
                        <div className='flex flex-row font-raleway font-medium text-left text-[#8A8A8A] text-[14px] mx-2'>
                          <LocationIcon className='w-[10px] h-[14px] m-1 text-[#8A8A8A]' /> &nbsp;
                          {currentResource && currentResource.city ? `${currentResource.city}, `: ``} 
                          {currentResource && currentResource.county ? `${currentResource.county}, `: ``} 
                          {currentResource && currentResource.state ? `${currentResource.state} `: ``}  
                          {currentResource && currentResource.zipcode ? `${currentResource.zipcode} `: ``}  
                          {
                            (
                              !currentResource.city && !currentResource.county && !currentResource.state && !currentResource.zipcode
                            ) ? 'Nationwide' : ''
                          }
                        </div>
                        {/* </span> */}
                      </div>
                    </div>
                      

                    <div className='w-11/12 mx-auto my-2'>
                      <PhotoGallery
                        photos={getResourcePhotos(currentResource)}
                        resourceName={currentResource?.name || ''}
                        resourceIcon={ResourceIcon}
                        onPhotoClick={(index) => {
                          setSelectedPhotoIndex(index)
                          setPhotoModalOpen(true)
                        }}
                      />
                    </div>
                    
                    <div className='flex flex-col w-full m-1'>
                      <div className='flex flex-col w-full space-y-2'>
                        {
                          resourceDetailsMenu.map((menuOption, i) => (
                            <div key={i} className='bg-white rounded-lg shadow-none border-0'>
                              {/* Accordion Header */}
                              <div 
                                className={`flex items-center justify-between p-4 cursor-pointer rounded-md hover:bg-gray-50 transition-colors bg-[#F8F8F8]`}
                                onClick={() => {
                                  if (currentDetailsMenu !== menuOption.name) {
                                    setcurrentDetailsMenu(menuOption.name)
                                  } else {
                                    setcurrentDetailsMenu("")
                                  }
                                  
                                }}
                              >
                                <div className='flex items-center space-x-3'>
                                  {/* Icon */}
                                  <div className='w-8 h-8 text-[#013847] bg-[#E6EBED] rounded-full flex items-center justify-center'>
                                    {menuOption.name === "description" && (
                                      <DescriptionIcon />
                                    )}
                                    {menuOption.name === "hours" && (
                                      <ClockIconFilled />
                                    )}
                                    {menuOption.name === "plans" && (
                                      <BookingPlanIcon />
                                    )}
                                    {menuOption.name === "tiers" && (
                                      <MembershipTiersIcon className='w-5 h-5' />
                                    )}
                                    {menuOption.name === "requirements" && (
                                      <RequirementsIcon className='w-[20px] h-[20px]' />
                                    )}
                                    {menuOption.name === "inventory" && (
                                      <InventoryIcon />
                                    )}
                                    {menuOption.name === "reviews" && (
                                      <ReviewsIcon />
                                    )}
                                    {menuOption.name === "schedules" && (
                                      <SchedulesIcon />
                                    )}
                                    {menuOption.name === "rules" && (
                                      <RulesIcon />
                                    )}
                                  </div>
                                  {/* Title */}
                                  <span className='text-black font-raleway font-medium text-base'>
                                    {capitalizeString(menuOption.label)}
                                  </span>
                                </div>
                                {/* Chevron Icon */}
                                <div className='flex items-center'>
                                  <svg 
                                    className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
                                      currentDetailsMenu === menuOption.name ? 'rotate-180' : ''
                                    }`} 
                                    fill="currentColor" 
                                    viewBox="0 0 20 20"
                                  >
                                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                  </svg>
                                </div>
                              </div>

                              {/* Accordion Content */}
                              {currentDetailsMenu === menuOption.name && (
                                <div className='px-4 pb-4 border-0'>
                                  <div className='pt-4'>
                                    {menuOption.name === "description" && (
                                      <div className='text-gray-700 font-open-sans text-base leading-relaxed'>
                                      {
                                        (currentResource && currentResource.description) ? (
                                            <p>{capitalizeString(currentResource.description)}</p>
                                          ) : (
                                            <div className='text-gray-400 text-sm'>
                                              No description to show
                                            </div>
                                          )
                                        }
                                        
                                        {/* Cost information for description */}
                                        {(["documentation", "training", "expert"].includes(resourceType as ResourceType) && currentResource?.total_amount) ? (
                                          <div className='mt-3 flex items-center text-gray-700 font-open-sans'>
                                            <span className='text-base'>{resourceType ? capitalizeString(resourceType) : 'Resource'} cost:</span>
                                            <span className='font-semibold text-base ml-1'>${styleThousandCommas(currentResource?.total_amount?.toFixed(2))}</span>
                                          </div>
                                        ) : (<></>)}

                                        {(resourceType === "funding" && currentResource?.total_amount) ? (
                                          <div className='mt-3 flex items-center text-gray-700 font-open-sans'>
                                            <span className='text-base'>{resourceType ? capitalizeString(resourceType) : 'Resource'} amount:</span>
                                            <span className='font-semibold text-base ml-1'>${styleThousandCommas(currentResource?.total_amount?.toFixed(2))}</span>
                                          </div>
                                        ) : (<></>)}
                                      </div>
                                    )}

                                    {menuOption.name === "hours" && (["farm", "kitchen"].includes(currentResource.resource_type as string)) && (
                                      <OperatingHoursGrid currentResourceOptions={currentResourceOptions} />
                                    )}

                                    {menuOption.name === "inventory" && (["farm", "kitchen"].includes(currentResource.resource_type as string)) && (
                                      <ResourceInventoryGrid 
                                        availableItems={availableItems as InventorySchema[]} 
                                        loggedInUid={loggedInUid}
                                        setSignInModalIsOpen={setSignInModalIsOpen}
                                        toggleApplyModal={handleToggleApplyModal}
                                        currentResource={currentResource}
                                        resourceBookings={resourceBookings}
                                      />
                                    )}

                                    {menuOption.name === "reviews" && (["farm", "kitchen", "training", "expert"].includes(currentResource.resource_type as string)) && (
                                      <ReviewsGrid reviews={[]} />
                                    )}

                                    {menuOption.name === "schedules" && (["training", "expert"].includes(currentResource.resource_type as string)) && (
                                      <ResourceScheduleGrid availableDates={availableDates as TrainingSchema[]} />
                                    )}

                                    {menuOption.name === "tiers" && currentResource.resource_type === "kitchen" && (
                                      <MembershipTiersGrid setSelectedPlan={setSelectedPlan} loggedInUid={loggedInUid} setSignInModalIsOpen={setSignInModalIsOpen} toggleApplyModal={handleToggleApplyModal} toggleMembershipModal={toggleMembershipModal} currentResource={currentResource} currentMember={currentMember as MembershipSchema} />
                                    )}
                                    {menuOption.name === "plans" && currentResource.resource_type === "kitchen" && (
                                      <BookingPlanGrid setSelectedPlan={setSelectedPlan} loggedInUid={loggedInUid} setSignInModalIsOpen={setSignInModalIsOpen} toggleApplyModal={handleToggleApplyModal} toggleMembershipModal={toggleMembershipModal} currentResource={currentResource} currentMember={currentMember as MembershipSchema} />
                                    )}

                                    {menuOption.name === "requirements" && currentResource.resource_type === "kitchen" && (
                                      <RequirementsGrid loggedInUid={loggedInUid} currentResource={currentResource} setSignInModalIsOpen={setSignInModalIsOpen} toggleAddingCredential={toggleAddingCredential} />
                                    )}

                                    {menuOption.name === "rules" && currentResource.resource_type === "kitchen" && (
                                      <div className='text-gray-700 text-base leading-relaxed'>
                                        <div className='space-y-3'>
                                          {currentResource.rules && currentResource.rules.length > 0 && currentResource.rules.map((rule, i) => (
                                            <div key={i} className='text-gray-400 text-sm'>
                                              {rule}
                                            </div>
                                          ))}
                                          {(!currentResource.rules || currentResource.rules.length < 1) && (
                                            <div className='text-gray-400 text-sm'>
                                              Kitchen rules and guidelines will be displayed here.
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))
                        }
                      </div>  

                      {/* Additional Action Buttons */}
                      <div className='flex flex-col mt-4 mx-auto w-11/12 space-y-2'>
                        {
                          (currentResource && currentResource.active && currentResource.requires_membership) ? (
                            <button 
                              className='flex rounded-md flex-row items-center justify-center text-white border-0 px-3 bg-[#013847] font-bold cursor-pointer h-[40px] text-center'
                              onClick={() => {
                                if (!loggedInUid){
                                  setSignInModalIsOpen(true)
                                } else if (currentMember && currentMember.status) {
                                  toggleMembershipModal("manage")
                                } else {
                                  toggleMembershipModal("request")
                                }
                              }}
                            >
                              {((currentMember && currentMember.status)) ? 'Manage' : 'Request'} Membership
                            </button>
                          ) : (<></>)
                        }
                        {
                          ((currentResource && currentResource.active && actionButtonLabel) && loggedInUid && ((!loggedInUid || (currentMember && currentMember.status)) || !currentResource.requires_membership) && actionButtonLabel !== "Book") ? (
                                        <button 
                                          onClick={()=> {
                                            if (!loggedInUid){
                                                setSignInModalIsOpen(true)
                                            }
                                            toggleApplyModal(resourceType)

                                          }} 
                              className='bg-primaryDark-0 text-white text-[16px] font-extrabold h-[40px] rounded-md border-0'>
                                            {actionButtonLabel}
                                        </button>
                                      ) : (
                                        <></>
                                      )
                                    }
                        </div>

                      {/* Action Buttons Row - 4 Icon Buttons */}
                      <div className='flex flex-row justify-center items-center gap-2 sm:gap-3 mt-6 mb-4 w-full'>
                        {/* Save Button */}
                        <button
                          onClick={() => {
                            if (!resourceIsSaved) {
                              handleSaveResource()
                            } else {
                              handleRemoveSavedResource()
                            }
                          }}
                          className='flex items-center justify-center w-[78px] h-[51px] rounded-lg bg-white border border-[#E9E9E9] hover:bg-[#E9E9E9] active:bg-[#E0E0E0] transition-colors cursor-pointer touch-manipulation'
                          aria-label={resourceIsSaved ? 'Unsave resource' : 'Save resource'}
                        >
                          {resourceIsSaved ? (
                            <HeartIcon className='w-6 h-6 text-[#013847]' style={{ width: '24px', height: '24px' }} />
                          ) : (
                            <HeartOutlineIcon className='w-6 h-6 text-[#013847]' style={{ width: '24px', height: '24px' }} />
                          )}
                        </button>

                        {/* Website Button */}
                        <button
                          onClick={() => openInNewTab(currentResource?.website ? currentResource.website : '')}
                          className={`flex items-center justify-center w-[78px] h-[51px] rounded-lg bg-white border border-[#E9E9E9] hover:bg-[#E9E9E9] active:bg-[#E0E0E0] transition-colors touch-manipulation ${
                            !currentResource?.website ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                          }`}
                          aria-label='Visit website'
                          disabled={!currentResource?.website}
                        >
                          <WebsiteIcon className='w-6 h-6 text-[#013847]' style={{ width: '24px', height: '24px' }} />
                        </button>

                        {/* Chat Button */}
                        <button
                          onClick={() => {
                            if (!loggedInUid) {
                              setSignInModalIsOpen(true)
                            } else {
                              if (communityId) {
                                setMessageCommunityId(communityId);
                              }
                              setMessagesOpen(true)
                            }
                          }}
                          className='flex items-center justify-center w-[78px] h-[51px] rounded-lg bg-white border border-[#E9E9E9] hover:bg-[#E9E9E9] active:bg-[#E0E0E0] transition-colors cursor-pointer touch-manipulation'
                          aria-label='Chat'
                        >
                          <ChatOutlineIcon className='w-6 h-6 text-[#013847]' style={{ width: '24px', height: '24px' }} />
                        </button>

                        {/* Share Button */}
                        <button
                          onClick={handleShare}
                          className='flex items-center justify-center w-[78px] h-[51px] rounded-lg bg-white border border-[#E9E9E9] hover:bg-[#E9E9E9] active:bg-[#E0E0E0] transition-colors cursor-pointer touch-manipulation'
                          aria-label='Share resource'
                        >
                          <PostShareIcon className='w-6 h-6 text-[#013847]' style={{ width: '24px', height: '24px' }} />
                        </button>
                      </div>
                    </div>
                </>
            </div>
        </div>

        <RequestMembershipModal formType={membershipModal} resourceDetail={currentResource} selectedPlan={selectedMembershipPlan} closeAppModal={()=>toggleMembershipModal(null)} />

        {
          (currentMember && currentMember?.status) ? (
        <RequestKitchenModal selectedPlan={selectedPlan || {} as ResourcePlanSchema} isOpen={!!loggedInUid && applyModal==="kitchen"} resourceDetail={currentResource} inventoryOnlyItem={inventoryOnlyItem} handleSubmitRequest={handleSubmitRequest} handleSubmitMultipleRequest={handleSubmitMultipleRequest} closeAppModal={closeApplyModal} />
          ) : (
            <></>
          )
        }
        <RegisterTrainingModal isOpen={!!loggedInUid && applyModal==="training"} resourceDetail={currentResource} handleSubmitRequest={handleSubmitRequest} closeAppModal={()=>toggleApplyModal(undefined)} />
        <ObtainDocumentationModal isOpen={!!loggedInUid && applyModal==="documentation"} resourceDetail={currentResource} handleSubmitRequest={handleSubmitRequest} closeAppModal={()=>toggleApplyModal(undefined)} />
        <ApplyFundingModal isOpen={!!loggedInUid && applyModal==="funding"} resourceDetail={currentResource} handleSubmitRequest={handleSubmitRequest} closeAppModal={()=>toggleApplyModal(undefined)} />
        <ScheduleExpertModal isOpen={!!loggedInUid && applyModal==="expert"} resourceDetail={currentResource} handleSubmitRequest={handleSubmitRequest} closeAppModal={()=>toggleApplyModal(undefined)} />
        <OrderInventoryModal isOpen={!!loggedInUid && applyModal==="farm"} resourceDetail={currentResource} handleSubmitRequest={handleSubmitRequest} closeAppModal={()=>toggleApplyModal(undefined)}  />
        <PhotoPreviewModal
          isOpen={photoModalOpen}
          photos={getResourcePhotos(currentResource)}
          currentIndex={selectedPhotoIndex}
          resourceName={currentResource?.name || ''}
          onClose={() => setPhotoModalOpen(false)}
          onNavigate={(index: number) => setSelectedPhotoIndex(index)}
        />
        <AddCredentialModal isOpen={addingCredential.status} closeAppModal={() => toggleAddingCredential({ status: false })} handleSubmitCredential={handleSubmitCredential} requirement={addingCredential.requirement as CurrentRequirementsSchema} />
      </>
    )
  }
}


export default ResourceDetails


type MembershipTiersGridProps = {
  currentResource: ResourceSchema
  currentMember: MembershipSchema
  toggleApplyModal: (resourceType: ResourceType) => void
  toggleMembershipModal: (membershipModal: "request" | "manage", plan?: PlanSchema) => void
  setSignInModalIsOpen: (isOpen: boolean) => void
  setSelectedPlan: React.Dispatch<React.SetStateAction<ResourcePlanSchema>>
  loggedInUid: string
}

/** Renders community membership tiers available for the kitchen resource */
const MembershipTiersGrid: React.FC<MembershipTiersGridProps> = ({currentResource, currentMember, setSelectedPlan, toggleApplyModal, toggleMembershipModal, setSignInModalIsOpen, loggedInUid}) => {

  const { allPlans } = useLoadMembershipPlans(currentResource?.community_id || "")
  const currentMembershipPlans: PlanSchema[] = allPlans ? allPlans : []


  return currentMembershipPlans.length > 0 ? (
    <div className={`flex flex-col my-4 font-open-sans w-full h-auto`}>
      <div className='flex flex-col space-y-4 w-full'>
        {
           currentMembershipPlans.map((plan: PlanSchema, i: number) => (
            <div key={i} className='w-full'>
                <MembershipTierCard 
                  currentMember={currentMember} 
                  plan={plan}
                  resourceDetail={currentResource}
                  toggleMembershipModal={toggleMembershipModal}
                  toggleApplyModal={(resourceType, plan) => {
                    toggleApplyModal(resourceType)
                    setSelectedPlan(plan as ResourcePlanSchema)
                  }} 
                  loggedInUid={loggedInUid} 
                  setSignInModalIsOpen={setSignInModalIsOpen} 
                />
            </div>
          ))
        }
      </div>
    </div>
  ) : (
    <div className={`flex flex-col text-[14px] text-[#aaa] my-4 w-full h-auto font-raleway`}>No tiers to show</div>
  )
} 


type MembershipTierCardProps = {
  currentMember: MembershipSchema
  plan: PlanSchema
  resourceDetail?: ResourceSchema
  toggleApplyModal: (resourceType: ResourceType, plan?: ResourcePlanSchema) => void
  toggleMembershipModal: (membershipModal: "request" | "manage", plan?: PlanSchema) => void
  loggedInUid: string
  setSignInModalIsOpen: (isOpen: boolean) => void
}

/** Membership tier card with dual-plan booking coverage and 24/7 access indicators */
const MembershipTierCard: React.FC<MembershipTierCardProps> = ({currentMember, plan, resourceDetail, toggleApplyModal, toggleMembershipModal, loggedInUid, setSignInModalIsOpen}) => {
  const [isSwitchPlanModalOpen, setIsSwitchPlanModalOpen] = useState<boolean>(false)

  const standardPeriodStyles = getPeriodStyles('STANDARD')
  const offPeakPeriodStyles = getPeriodStyles('OFF_PEAK')
  const peakPeriodStyles = getPeriodStyles('PEAK')
  const isDualPlan = plan.plan_type === 'dual'
  const dualResourceAssociations = isDualPlan ? (plan.associated_resources || []) : []


  return (
    <>
    <div className='bg-white rounded-lg border-[1px] border-[#E9E9E9] p-4 sm:p-6 shadow-sm w-full'>
      {/* Header Section */}
      <div className='mb-4 sm:mb-6'>
        <div className='flex flex-wrap items-center gap-2 mb-2'>
          <h3 className='text-[18px] sm:text-[20px] font-semibold text-gray-800 font-raleway'>{plan.plan_name}</h3>
          {isDualPlan && (
            <span
              className='inline-flex items-center gap-1 text-[12px] font-raleway font-medium text-primaryDark-300 bg-primaryDark-400/10 rounded-md px-2 py-1'
              data-testid='dual-plan-badge'
            >
              <BookingPlanIcon className='w-3.5 h-3.5 shrink-0' aria-hidden />
              Covers Booking
            </span>
          )}
          {plan.is_fulltime_hours && (
            <span
              className='inline-flex items-center gap-1 text-[12px] font-raleway font-medium text-emerald-700 bg-emerald-50 rounded-md px-2 py-1'
              data-testid='fulltime-hours-badge'
            >
              <ClockIconFilled className='w-3.5 h-3.5 shrink-0 text-emerald-600' aria-hidden />
              24/7 Access
            </span>
          )}
        </div>
        <p className='text-[13px] sm:text-[14px] text-[#4D4D4D] font-open-sans'>{plan.plan_description}</p>
        {dualResourceAssociations.length > 0 && (
          <div className='mt-3 space-y-1.5' data-testid='dual-plan-resources'>
            {dualResourceAssociations.map((association, associationIndex) => (
              <div
                key={`dual-resource-${association.resource_id || associationIndex}`}
                className='flex items-center gap-2 text-[13px] text-gray-700 font-open-sans'
              >
                <NavResourcesIconDark className='w-3.5 h-3.5 text-primaryDark-300 shrink-0' aria-hidden />
                <span className='truncate'>
                  {association.resource_name || resourceDetail?.name || 'Linked resource'}
                  {association.max_duration > 0 && (
                    <span className='text-[#808080]'>
                      {' '}· up to {association.max_duration} {association.max_duration === 1 ? 'hr' : 'hrs'}
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Rates and Details Grid - Responsive: 2 cols on mobile, 3 on larger screens */}
      <div className='grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6'>
        {/* Standard Rate */}
        <div className='bg-[#FAFAFA] rounded-lg py-3 px-2 sm:px-3'>
          <div className='flex items-center mb-2'>
            <div className={`w-2 h-2 ${standardPeriodStyles.dot} rounded-full mr-2 flex-shrink-0`}></div>
            <span className='text-xs sm:text-sm text-[#808080] font-raleway'>Standard Rate</span>
          </div>
          <p className='text-[16px] sm:text-[18px] font-semibold text-black font-raleway'>${styleThousandCommas(plan.plan_rate || 0)}/{planRecurrenceMap[plan?.plan_recurrence as keyof typeof planRecurrenceMap]}</p>
        </div>

        {/* Off-Peak Rate */}
        {
          ((plan.plan_recurrence === "hourly" || plan.plan_recurrence === "daily") && plan.pricing_type === "dynamic") ? (
            <div className='bg-[#FAFAFA] rounded-lg py-3 px-2 sm:px-3'>
              <div className='flex items-center mb-2'>
                <div className={`w-2 h-2 ${offPeakPeriodStyles.dot} rounded-full mr-2 flex-shrink-0`}></div>
                <span className='text-xs sm:text-sm text-[#808080] font-raleway'>Off-Peak Rate</span>
              </div>
            </div>
          ) : (
            <></>
          )
        }
        
        {/* Peak Rate */}
        {
          ((plan.plan_recurrence === "hourly" || plan.plan_recurrence === "daily") && plan.pricing_type === "dynamic") ? (
            <div className='bg-[#FAFAFA] rounded-lg py-3 px-2 sm:px-3'>
          <div className='flex items-center mb-2'>
            <div className={`w-2 h-2 ${peakPeriodStyles.dot} rounded-full mr-2 flex-shrink-0`}></div>
            <span className='text-xs sm:text-sm text-[#808080] font-raleway'>Peak Rate</span>
          </div>
        </div>) : (
            <></>
          )
        }

        {/* Max Hours */}
        {(plan.plan_max_hours) ? (<div className='bg-[#FAFAFA] rounded-lg py-3 px-2 sm:px-3'>
          <div className='flex items-center mb-2'>
            <ClockIconFilled className='w-3 h-3 sm:w-4 sm:h-4 text-primaryDark-400 mr-2 flex-shrink-0' />
            <span className='text-xs sm:text-sm text-[#808080] font-raleway'>
              {(["weekly", "monthly", "annual"].includes(plan.plan_recurrence as any)) ? "Max Days" : "Max Hours"}
            </span>
          </div>
          <p className='text-[16px] sm:text-[18px] font-semibold text-black font-raleway'>
            {(["weekly", "monthly", "annual"].includes(plan.plan_recurrence as any)) 
              ? Math.floor((plan.plan_max_hours || 0) / 24) 
              : (plan.plan_max_hours || 0)
            }
          </p>
        </div>) : (<></>)}
      </div>

      {/* Action Buttons - Stack vertically on mobile */}
      <div className='flex flex-col sm:flex-row gap-3'>
        <button 
          onClick={()=>setIsSwitchPlanModalOpen(true)} 
          className='w-full sm:flex-1 flex font-semibold items-center justify-center gap-2 py-3 px-4 bg-white border border-[#E9E9E9] rounded-lg text-gray-700 font-raleway hover:bg-gray-50 transition-colors text-sm sm:text-base'
        >
          <VisibilityOutlinedIcon className='text-[#A1A1A1]' fontSize='small' />
          <span className='hidden sm:inline'>More Plan Details</span>
          <span className='sm:hidden'>View Plan Details</span>
        </button>
        <button 
          onClick={()=>{
            if (!loggedInUid) {
              setSignInModalIsOpen(true)
              } else {
                toggleMembershipModal("request", plan)
            }
          }}
          className='w-full sm:flex-1 border-0 font-semibold flex items-center justify-center gap-2 py-3 px-4 bg-primaryDark-100 text-white rounded-lg font-raleway hover:bg-primaryDark-300 transition-colors text-sm sm:text-base'
        >
          <MembershipTiersIcon className='w-4 h-4 sm:w-5 sm:h-5' />
          Start Membership
        </button>
      </div>
    </div>
    <MorePlanDetailsModal key={plan.plan_id} isOpen={isSwitchPlanModalOpen} setIsOpen={setIsSwitchPlanModalOpen} closeAppModal={()=>setIsSwitchPlanModalOpen(false)} plan_details={plan as ResourcePlanSchema} resourceDetail={resourceDetail} />
    </>
  )
} 


type BookingPlanGridProps = {
  currentResource: ResourceSchema
  currentMember: MembershipSchema
  toggleApplyModal: (resourceType: ResourceType) => void
  toggleMembershipModal: (membershipModal: "request" | "manage", plan?: PlanSchema) => void
  setSignInModalIsOpen: (isOpen: boolean) => void
  setSelectedPlan: React.Dispatch<React.SetStateAction<ResourcePlanSchema>>
  loggedInUid: string
}

const BookingPlanGrid: React.FC<BookingPlanGridProps> = ({currentResource, currentMember, setSelectedPlan, toggleApplyModal, toggleMembershipModal, setSignInModalIsOpen, loggedInUid}) => {

  // const [currentFilter, setCurrentFilter] = useState<string>("All")

  const currentMemberResourceId = currentResource?.id || ''
  const currentMemberBookingPlanId = getMemberBookingPlanIdForResource(
    currentMember,
    currentMemberResourceId,
    currentResource?.plans
  )


  return (currentResource?.plans && currentResource?.plans?.length > 0) ? (
    <div className={`flex flex-col my-4 font-open-sans w-full h-auto`}>
      <div className='flex flex-col space-y-4 w-full'>
        {
           currentResource.plans.map((plan: ResourcePlanSchema, i: number) => (
            <div key={i} className='w-full'>
              {
                (plan?.plan_id && currentMemberBookingPlanId && currentMemberBookingPlanId === plan.plan_id) ? (
                  <CurrentBookingPlanCard 
                    currentMember={currentMember} 
                    plan={plan}
                    resourceDetail={currentResource}
                    toggleApplyModal={(resourceType, plan) => {
                      toggleApplyModal(resourceType)
                      setSelectedPlan(plan as ResourcePlanSchema)
                    }} 
                    loggedInUid={loggedInUid} 
                    setSignInModalIsOpen={setSignInModalIsOpen} 
                  />
                ) : (
                  <BookingPlanCard 
                    currentMember={currentMember} 
                    plan={plan}
                    resourceDetail={currentResource}
                    toggleMembershipModal={toggleMembershipModal}
                    toggleApplyModal={(resourceType, plan) => {
                      toggleApplyModal(resourceType)
                      setSelectedPlan(plan as ResourcePlanSchema)
                    }} 
                    loggedInUid={loggedInUid} 
                    setSignInModalIsOpen={setSignInModalIsOpen} />
                )
              }
            </div>
          ))
        }
      </div>
    </div>
  ) : (
    <div className={`flex flex-col text-[14px] text-[#aaa] my-4 w-full h-auto font-raleway`}>No plan to show</div>
  )
} 


type BookingPlanCardProps = {
  currentMember: MembershipSchema
  plan: ResourcePlanSchema
  resourceDetail?: ResourceSchema
  toggleApplyModal: (resourceType: ResourceType, plan?: ResourcePlanSchema) => void
  toggleMembershipModal: (membershipModal: "request" | "manage", plan?: PlanSchema) => void
  loggedInUid: string
  setSignInModalIsOpen: (isOpen: boolean) => void
}

const BookingPlanCard: React.FC<BookingPlanCardProps> = ({currentMember, plan, resourceDetail, toggleApplyModal, toggleMembershipModal, loggedInUid, setSignInModalIsOpen}) => {
  const [isSwitchPlanModalOpen, setIsSwitchPlanModalOpen] = useState<boolean>(false)

  const standardPeriodStyles = getPeriodStyles('STANDARD')
  const offPeakPeriodStyles = getPeriodStyles('OFF_PEAK')
  const peakPeriodStyles = getPeriodStyles('PEAK')
  const isDualPlan = plan.plan_type === 'dual'

  // (plan?.plan_id && currentMember?.booking_plans && currentMember.booking_plans.includes(plan.plan_id as string))
  // const currentPlan = (plan?.plan_id && currentMember?.booking_plans && currentMember.booking_plans.find((p: {plan_id: string}) => p.plan_id === plan.plan_id))

  return (
    <>
    <div className='bg-white rounded-lg border-[1px] border-[#E9E9E9] p-4 sm:p-6 shadow-sm w-full'>
      {/* Header Section */}
      <div className='mb-4 sm:mb-6'>
        <div className='flex flex-wrap items-center gap-2 mb-2'>
          <h3 className='text-[18px] sm:text-[20px] font-semibold text-gray-800 font-raleway'>{plan.plan_name}</h3>
          {isDualPlan && (
            <span
              className='inline-flex items-center gap-1 text-[12px] font-raleway font-medium text-primaryDark-300 bg-primaryDark-400/10 rounded-md px-2 py-1'
              data-testid='dual-plan-badge'
            >
              <MembershipTiersIcon className='w-3.5 h-3.5 shrink-0' aria-hidden />
              Included with Membership
            </span>
          )}
          {plan.is_fulltime_hours && (
            <span
              className='inline-flex items-center gap-1 text-[12px] font-raleway font-medium text-emerald-700 bg-emerald-50 rounded-md px-2 py-1'
              data-testid='fulltime-hours-badge'
            >
              <ClockIconFilled className='w-3.5 h-3.5 shrink-0 text-emerald-600' aria-hidden />
              24/7 Access
            </span>
          )}
        </div>
        <p className='text-[13px] sm:text-[14px] text-[#4D4D4D] font-open-sans'>{plan.plan_description}</p>
      </div>

      {/* Rates and Details Grid - Responsive: 2 cols on mobile, 3 on larger screens */}
      <div className='grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6'>
        {/* Standard Rate */}
        <div className='bg-[#FAFAFA] rounded-lg py-3 px-2 sm:px-3'>
          <div className='flex items-center mb-2'>
            <div className={`w-2 h-2 ${standardPeriodStyles.dot} rounded-full mr-2 flex-shrink-0`}></div>
            <span className='text-xs sm:text-sm text-[#808080] font-raleway'>Standard Rate</span>
          </div>
          <p className='text-[16px] sm:text-[18px] font-semibold text-black font-raleway'>${styleThousandCommas(plan.plan_rate || 0)}/{planRecurrenceMap[plan?.plan_recurrence as keyof typeof planRecurrenceMap]}</p>
        </div>

        {/* Off-Peak Rate */}
        {
          ((plan.plan_recurrence === "hourly" || plan.plan_recurrence === "daily") && plan.pricing_type === "dynamic") ? (
            <div className='bg-[#FAFAFA] rounded-lg py-3 px-2 sm:px-3'>
              <div className='flex items-center mb-2'>
                <div className={`w-2 h-2 ${offPeakPeriodStyles.dot} rounded-full mr-2 flex-shrink-0`}></div>
                <span className='text-xs sm:text-sm text-[#808080] font-raleway'>Off-Peak Rate</span>
              </div>
              <p className='text-[16px] sm:text-[18px] font-semibold text-black font-raleway'>${styleThousandCommas(plan.off_peak_rate || plan.plan_rate || 0)}/{planRecurrenceMap[plan?.plan_recurrence as keyof typeof planRecurrenceMap]}</p>
            </div>
          ) : (
            <></>
          )
        }
        
        {/* Peak Rate */}
        {
          ((plan.plan_recurrence === "hourly" || plan.plan_recurrence === "daily") && plan.pricing_type === "dynamic") ? (
            <div className='bg-[#FAFAFA] rounded-lg py-3 px-2 sm:px-3'>
          <div className='flex items-center mb-2'>
            <div className={`w-2 h-2 ${peakPeriodStyles.dot} rounded-full mr-2 flex-shrink-0`}></div>
            <span className='text-xs sm:text-sm text-[#808080] font-raleway'>Peak Rate</span>
          </div>
          <p className='text-[16px] sm:text-[18px] font-semibold text-black font-raleway'>${styleThousandCommas(plan.peak_rate || plan.plan_rate || 0)}/{planRecurrenceMap[plan?.plan_recurrence as keyof typeof planRecurrenceMap]}</p>
        </div>) : (
            <></>
          )
        }

        {/* Max Hours */}
        {(plan.plan_max_hours) ? (<div className='bg-[#FAFAFA] rounded-lg py-3 px-2 sm:px-3'>
          <div className='flex items-center mb-2'>
            <ClockIconFilled className='w-3 h-3 sm:w-4 sm:h-4 text-primaryDark-400 mr-2 flex-shrink-0' />
            <span className='text-xs sm:text-sm text-[#808080] font-raleway'>
              {(["weekly", "monthly", "annual"].includes(plan.plan_recurrence as any)) ? "Max Days" : "Max Hours"}
            </span>
          </div>
          <p className='text-[16px] sm:text-[18px] font-semibold text-black font-raleway'>
            {(["weekly", "monthly", "annual"].includes(plan.plan_recurrence as any)) 
              ? Math.floor((plan.plan_max_hours || 0) / 24) 
              : (plan.plan_max_hours || 0)
            }
          </p>
        </div>) : (<></>)}

        {/* Overage */}
        {(plan?.plan_max_hours && plan.overage_rate ) ? (
          <div className='bg-[#FAFAFA] rounded-lg py-3 px-2 sm:px-3'>
          <div className='flex items-center mb-2'>
            <PlanIcon className='w-3 h-3 sm:w-4 sm:h-4 text-primaryDark-400 mr-2 flex-shrink-0' />
            <span className='text-xs sm:text-sm text-[#808080] font-raleway'>Overage</span>
          </div>
          <p className='text-[16px] sm:text-[18px] font-semibold text-black font-raleway'>${styleThousandCommas(plan.overage_rate)}/hour</p>
        </div>):(
          <></>
        )}

      </div>

      {/* Action Buttons - Stack vertically on mobile */}
      <div className='flex flex-col sm:flex-row gap-3'>
        <button 
          onClick={()=>setIsSwitchPlanModalOpen(true)} 
          className='w-full sm:flex-1 flex font-semibold items-center justify-center gap-2 py-3 px-4 bg-white border border-[#E9E9E9] rounded-lg text-gray-700 font-raleway hover:bg-gray-50 transition-colors text-sm sm:text-base'
        >
          <VisibilityOutlinedIcon className='text-[#A1A1A1]' fontSize='small' />
          <span className='hidden sm:inline'>More Plan Details</span>
          <span className='sm:hidden'>View Plan Details</span>
        </button>
        <button 
          onClick={()=>{
            if (!loggedInUid){
              setSignInModalIsOpen(true)
              } else if(!currentMember || !currentMember?.status) {
                toggleMembershipModal("request", isDualPlan ? plan : undefined)
              } else {
              toggleApplyModal(plan.resource_type as ResourceType, plan)
            }
          }}
          className='w-full sm:flex-1 border-0 font-semibold flex items-center justify-center gap-2 py-3 px-4 bg-primaryDark-100 text-white rounded-lg font-raleway hover:bg-primaryDark-300 transition-colors text-sm sm:text-base'
        >
          <BookingPlanIcon className='w-4 h-4 sm:w-5 sm:h-5' />
          Start With Plan
        </button>
      </div>
    </div>
    <MorePlanDetailsModal key={plan.plan_id} isOpen={isSwitchPlanModalOpen} setIsOpen={setIsSwitchPlanModalOpen} closeAppModal={()=>setIsSwitchPlanModalOpen(false)} plan_details={plan} resourceDetail={resourceDetail} />
    </>
  )
} 



type CurrentBookingPlanCardProps = {
  currentMember: MembershipSchema
  plan: ResourcePlanSchema
  resourceDetail?: ResourceSchema
  toggleApplyModal: (resourceType: ResourceType, plan?: ResourcePlanSchema) => void
  loggedInUid: string
  setSignInModalIsOpen: (isOpen: boolean) => void
}

const CurrentBookingPlanCard: React.FC<CurrentBookingPlanCardProps> = ({currentMember, plan, resourceDetail, toggleApplyModal, loggedInUid, setSignInModalIsOpen}) => {
  const [isSwitchPlanModalOpen, setIsSwitchPlanModalOpen] = useState<boolean>(false)

  const standardPeriodStyles = getPeriodStyles('STANDARD')
  const offPeakPeriodStyles = getPeriodStyles('OFF_PEAK')
  const peakPeriodStyles = getPeriodStyles('PEAK')
  const isDualPlan = plan.plan_type === 'dual'

  const currentMemberResourceId = plan?.resource_id || ''
  const currentMemberBookingBalance = getMemberBookingBalanceForResource(
    currentMember,
    currentMemberResourceId,
    plan.plan_type
  )

  return (
    <>
    <div className='flex w-full flex-row bg-primaryDark-300 text-white text-[14px] sm:text-[16px] h-10 rounded-t-lg mx-auto justify-center items-center px-4'>
      <p className='font-raleway font-semibold'>Current Plan</p>
      {
        (currentMemberBookingBalance && !['hourly', 'daily'].includes(plan?.plan_recurrence || '')) ? (
          <div className='flex flex-row font-open-sans text-xs sm:text-[14px] font-medium ml-auto'><span className='font-bold font-raleway my-auto'>Booking Balance:&nbsp;</span> {styleThousandCommas(currentMemberBookingBalance)} hours</div>
        ) : (<></>)
      }
    </div>
    <div className='bg-white rounded-b-lg border-[1px] border-[#E9E9E9] p-4 sm:p-6 shadow-sm w-full'>
      {/* Header Section */}
      <div className='mb-4 sm:mb-6'>
        <div className='flex flex-wrap items-center gap-2 mb-2'>
          <h3 className='text-[18px] sm:text-[20px] font-semibold text-gray-800 font-raleway'>{plan.plan_name}</h3>
          {isDualPlan && (
            <span
              className='inline-flex items-center gap-1 text-[12px] font-raleway font-medium text-primaryDark-300 bg-primaryDark-400/10 rounded-md px-2 py-1'
              data-testid='dual-plan-badge'
            >
              <MembershipTiersIcon className='w-3.5 h-3.5 shrink-0' aria-hidden />
              Included with Membership
            </span>
          )}
          {plan.is_fulltime_hours && (
            <span
              className='inline-flex items-center gap-1 text-[12px] font-raleway font-medium text-emerald-700 bg-emerald-50 rounded-md px-2 py-1'
              data-testid='fulltime-hours-badge'
            >
              <ClockIconFilled className='w-3.5 h-3.5 shrink-0 text-emerald-600' aria-hidden />
              24/7 Access
            </span>
          )}
        </div>
        <p className='text-[13px] sm:text-[14px] text-[#4D4D4D] font-open-sans'>{plan.plan_description}</p>
      </div>

      {/* Rates and Details Grid - Responsive: 2 cols on mobile, 3 on larger screens */}
      <div className='grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6'>
        {/* Standard Rate */}
        <div className='bg-[#FAFAFA] rounded-lg py-3 px-2 sm:px-3'>
          <div className='flex items-center mb-2'>
            <div className={`w-2 h-2 ${standardPeriodStyles.dot} rounded-full mr-2 flex-shrink-0`}></div>
            <span className='text-xs sm:text-sm text-[#808080] font-raleway'>Standard Rate</span>
          </div>
          <p className='text-[16px] sm:text-[18px] font-semibold text-black font-raleway'>${styleThousandCommas(plan.plan_rate || 0)}/{planRecurrenceMap[plan?.plan_recurrence as keyof typeof planRecurrenceMap]}</p>
        </div>

        {/* Off-Peak Rate */}
        {
          ((plan.plan_recurrence === "hourly" || plan.plan_recurrence === "daily") && plan.pricing_type === "dynamic") ? (
            <div className='bg-[#FAFAFA] rounded-lg py-3 px-2 sm:px-3'>
              <div className='flex items-center mb-2'>
                <div className={`w-2 h-2 ${offPeakPeriodStyles.dot} rounded-full mr-2 flex-shrink-0`}></div>
                <span className='text-xs sm:text-sm text-[#808080] font-raleway'>Off-Peak Rate</span>
              </div>
              <p className='text-[16px] sm:text-[18px] font-semibold text-black font-raleway'>${styleThousandCommas(plan.off_peak_rate || plan.plan_rate || 0)}/{planRecurrenceMap[plan?.plan_recurrence as keyof typeof planRecurrenceMap]}</p>
            </div>
          ) : (
            <></>
          )
        }
        
        {/* Peak Rate */}
        {
          ((plan.plan_recurrence === "hourly" || plan.plan_recurrence === "daily") && plan.pricing_type === "dynamic") ? (
            <div className='bg-[#FAFAFA] rounded-lg py-3 px-2 sm:px-3'>
          <div className='flex items-center mb-2'>
            <div className={`w-2 h-2 ${peakPeriodStyles.dot} rounded-full mr-2 flex-shrink-0`}></div>
            <span className='text-xs sm:text-sm text-[#808080] font-raleway'>Peak Rate</span>
          </div>
          <p className='text-[16px] sm:text-[18px] font-semibold text-black font-raleway'>${styleThousandCommas(plan.peak_rate || plan.plan_rate || 0)}/{planRecurrenceMap[plan?.plan_recurrence as keyof typeof planRecurrenceMap]}</p>
        </div>) : (
            <></>
          )
        }

        {/* Max Hours */}
        {(plan.plan_max_hours) ? (<div className='bg-[#FAFAFA] rounded-lg py-3 px-2 sm:px-3'>
          <div className='flex items-center mb-2'>
            <ClockIconFilled className='w-3 h-3 sm:w-4 sm:h-4 text-primaryDark-400 mr-2 flex-shrink-0' />
            <span className='text-xs sm:text-sm text-[#808080] font-raleway'>
              {(["weekly", "monthly", "annual"].includes(plan.plan_recurrence as any)) ? "Max Days" : "Max Hours"}
            </span>
          </div>
          <p className='text-[16px] sm:text-[18px] font-semibold text-black font-raleway'>
            {(["weekly", "monthly", "annual"].includes(plan.plan_recurrence as any)) 
              ? Math.floor((plan.plan_max_hours || 0) / 24) 
              : (plan.plan_max_hours || 0)
            }
          </p>
        </div>) : (<></>)}

        {/* Overage */}
        {(plan?.plan_max_hours && plan.overage_rate ) ? (
          <div className='bg-[#FAFAFA] rounded-lg py-3 px-2 sm:px-3'>
          <div className='flex items-center mb-2'>
            <PlanIcon className='w-3 h-3 sm:w-4 sm:h-4 text-primaryDark-400 mr-2 flex-shrink-0' />
            <span className='text-xs sm:text-sm text-[#808080] font-raleway'>Overage</span>
          </div>
          <p className='text-[16px] sm:text-[18px] font-semibold text-black font-raleway'>${styleThousandCommas(plan.overage_rate)}/hour</p>
        </div>):(
          <></>
        )}

      </div>

      {/* Action Buttons - Stack vertically on mobile */}
      <div className='flex flex-col sm:flex-row gap-3'>
        <button 
          onClick={()=>setIsSwitchPlanModalOpen(true)} 
          className='w-full sm:flex-1 flex font-semibold items-center justify-center gap-2 py-3 px-4 bg-white border border-[#E9E9E9] rounded-lg text-gray-700 font-raleway hover:bg-gray-50 transition-colors text-sm sm:text-base'
        >
          <VisibilityOutlinedIcon className='text-[#A1A1A1]' fontSize='small' />
          <span className='hidden sm:inline'>More Plan Details</span>
          <span className='sm:hidden'>View Plan Details</span>
        </button>
        <button 
          onClick={()=>{
            if (!loggedInUid){
              setSignInModalIsOpen(true)
              } else {
              toggleApplyModal(plan.resource_type as ResourceType, plan)
            }
          }}
          className='w-full sm:flex-1 border-0 font-semibold flex items-center justify-center gap-2 py-3 px-4 bg-primaryDark-100/95 text-white rounded-lg font-raleway hover:bg-primaryDark-300 transition-colors text-sm sm:text-base'
        >
          <BookingPlanIcon className='w-4 h-4 sm:w-5 sm:h-5' />
          Start Booking
        </button>
      </div>
    </div>
    <MorePlanDetailsModal key={plan.plan_id} isOpen={isSwitchPlanModalOpen} setIsOpen={setIsSwitchPlanModalOpen} closeAppModal={()=>setIsSwitchPlanModalOpen(false)} plan_details={plan} resourceDetail={resourceDetail} />
    </>
  )
} 



// const CurrentBookingPlanCard: React.FC<CurrentBookingPlanCardProps> = ({plan}) => {
//   return (
//     <>
//     <div className='flex w-full flex-row bg-primaryDark-300 text-white text-[16px] font-medium text-center h-10 rounded-t-lg mx-auto justify-center items-center'>Current Plan</div>
//     <div className='flex flex-row rounded-lg rounded-t-none border-[1px] border-[#E9E9E9] p-2 h-[100px]'>
//       <div className='my-auto m-2'><PlanIcon /></div>
//       <div className='flex flex-col my-auto m-2'>
//           <p className='text-[18px] font-semibold text-black font-raleway'>{plan.plan_name}</p>
//           <p className='text-[16px] text-[#808080] font-raleway'>${styleThousandCommas(plan.plan_rate || 0)} {capitalizeString(plan?.plan_recurrence ? plan?.plan_recurrence : "")}</p>
//       </div>
//     </div>
//     </>
//   )
// } 


type RequirementsGridProps = {
  currentResource: ResourceSchema
  loggedInUid: string
  setSignInModalIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  toggleAddingCredential: React.Dispatch<React.SetStateAction<AddRequirementStatus>>
}

const RequirementsGrid: React.FC<RequirementsGridProps> = ({currentResource, loggedInUid, setSignInModalIsOpen, toggleAddingCredential}) => {

  const [currentFilter, setCurrentFilter] = useState<string>("All")
  const { memberRequirementStatuses } = useGetMemberRequirementsStatus(loggedInUid)
  
  // Get requirements status for this resource's community
  const communityId = currentResource?.community_id || ''
  const requirementsStatus: CurrentRequirementsSchema[] = (memberRequirementStatuses && communityId) 
    ? (memberRequirementStatuses[communityId] || [])
    : []

  // Create a map of requirement code/name to full status object
  const requirementStatusMap = new Map<string, CurrentRequirementsSchema>()
  requirementsStatus.forEach((reqStatus) => {
    if (reqStatus.code) {
      requirementStatusMap.set(reqStatus.code, reqStatus)
    }
    if (reqStatus.name) {
      requirementStatusMap.set(reqStatus.name, reqStatus)
    }
  })

  // Filter requirements based on currentFilter
  const filteredRequirements = currentResource?.requirements?.filter((requirement) => {
    if (currentFilter === "All") return true
    const reqStatus = requirementStatusMap.get(requirement.code) || requirementStatusMap.get(requirement.name)
    const isEffectivelyMet = reqStatus
      ? isRequirementEffectivelyMet(reqStatus)
      : false
    if (currentFilter === "Completed") return isEffectivelyMet
    if (currentFilter === "To Do") return !isEffectivelyMet
    return true
  }) || []

  return (currentResource?.requirements && currentResource?.requirements?.length > 0) ? (
    <div className={`flex flex-col my-4 font-open-sans w-full h-auto`}>
      {
        (loggedInUid) && (<div className='mb-4 font-raleway text-[14px] font-semibold'>
          {
            ["All", "Completed", "To Do"].map((filter, i) => (
              <span key={i} onClick={()=>setCurrentFilter(filter)} className={`mr-2 ${(filter === currentFilter) ? 'text-primaryDark-300' : 'text-[#808080] cursor-pointer hover:text-primaryDark-400'} `} >
                {filter} &nbsp;
              </span>
            ))
          }
        </div>)
      }
      <div className='flex flex-col space-y-4 text-black rounded-lg h-auto'>
        {
          filteredRequirements.map((requirement, i) => {
            const reqStatus = requirementStatusMap.get(requirement.code) || requirementStatusMap.get(requirement.name)
            return (
              <RequirementCard 
                key={i}
                requirement={requirement} 
                loggedInUid={loggedInUid}
                setSignInModalIsOpen={setSignInModalIsOpen}
                requirementStatus={reqStatus}
                toggleAddingCredential={toggleAddingCredential}
                communityId={currentResource?.community_id || ''}
              />
            )
          })
        }
      </div>
    </div>
  ) : (
    <div className={`flex flex-col text-[14px] text-[#aaa] my-4 w-full h-auto font-raleway`}>No requirement to show</div>
  )
} 



type RequirementCardProps = {
  requirement: RequirementSchema
  loggedInUid: string
  setSignInModalIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  requirementStatus?: CurrentRequirementsSchema
  toggleAddingCredential: React.Dispatch<React.SetStateAction<AddRequirementStatus>>
  communityId: string
}


const RequirementCard: React.FC<RequirementCardProps> = ({requirement, loggedInUid, setSignInModalIsOpen, requirementStatus, toggleAddingCredential, communityId}) => {
  const navigate = useNavigate()
  const statusDisplay = buildRequirementStatusDisplay({
    is_met: requirementStatus?.is_met || false,
    expiration: requirementStatus?.expiration || '',
    code: requirement.code,
    is_required: requirementStatus?.is_required ?? false,
    presigned_url: requirementStatus?.presigned_url,
    category: requirementStatus?.category ?? requirement.category,
  })
  const StatusIcon =
    statusDisplay.statusTone === 'success'
      ? CheckCircle
      : statusDisplay.statusTone === 'warning'
        ? ReportProblem
        : statusDisplay.statusTone === 'error'
          ? Error
          : null
  const statusIconColor =
    statusDisplay.statusTone === 'success'
      ? 'success'
      : statusDisplay.statusTone === 'warning'
        ? 'warning'
        : 'error'
  
  const handleFindDocumentation = () => {
    navigate("/documentation")
  }

  const handleUploadDocumentation = () => {
    if (!loggedInUid){
      setSignInModalIsOpen(true)
    } else {
      // Convert RequirementSchema to CurrentRequirementsSchema for the modal
      const currentRequirement: CurrentRequirementsSchema = {
        ...requirement,
        community_id: communityId,
        is_met: requirementStatus?.is_met || false,
        is_required: requirementStatus?.is_required || false,
        presigned_url: requirementStatus?.presigned_url,
        expiration: requirementStatus?.expiration || '',
        credential_number: requirementStatus?.credential_number || '',
        municipality: requirementStatus?.municipality || ''
      }
      toggleAddingCredential({ status: true, requirement: currentRequirement })
    }
  }

  const handleGoToPaymentMethod = () => {
    navigate('/wallet')
  }

  return (
    <div className={`flex flex-col rounded-lg border p-4 shadow-sm w-full ${statusDisplay.rowSurfaceClass}`}>
      {/* Top Row: Icon and Checkbox */}
      <div className='flex flex-row w-full items-start justify-between mb-3'>
        {/* Document Icon - Light Blue Square */}
        <div className={`flex items-center justify-center h-12 w-12 rounded-lg border-0 ${statusDisplay.iconBoxClass}`}>
          <RequirementsIcon className='h-6 w-6' style={{ fill: '#5DB6CE' }} />
        </div>
        
        {/* Checkbox - Top Right */}
        <div className='flex items-center justify-center'>
          {statusDisplay.isEffectivelyMet ? (
            <CheckBox className='text-primaryDark-300' style={{ fontSize: 24 }} />
          ) : (
            <CheckBoxOutlineBlank className='text-[#CFCFCF]' style={{ fontSize: 24 }} />
          )}
        </div>
      </div>

      {/* Requirement Title and Status */}
      <div className='mb-4'>
        <div className='flex flex-col w-full mb-2'>
          <p className='text-[16px] sm:text-[18px] font-semibold text-black font-raleway leading-tight'>
            {requirement.name}
          </p>
          {statusDisplay.detailLabel && (
            <p className={`text-[12px] font-open-sans mt-1 ${statusDisplay.subtitleTextClass}`}>
              {statusDisplay.detailLabel}
            </p>
          )}
          {statusDisplay.statusLabel && StatusIcon && (
            <div className='flex flex-row items-center mt-2'>
              <p className={`text-[12px] font-open-sans font-medium mr-1 ${statusDisplay.statusTextClass}`}>
                {statusDisplay.statusLabel}
              </p>
              <StatusIcon sx={{ fontSize: 18 }} color={statusIconColor as 'success' | 'warning' | 'error'} />
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons - Stacked Vertically */}
      <div className='flex flex-col space-y-3 w-full'>
        {/* How to get this? Button */}
        <button 
          onClick={handleFindDocumentation} 
          className='w-full py-3 px-4 rounded-lg bg-white border border-[#E9E9E9] text-[#013847] text-[16px] font-raleway font-semibold hover:bg-[#F8F8F8] transition-colors'
        >
          How to get this?
        </button>
        
        {/* Upload/Change/Payment Button */}
        {requirement.code === "PAY" ? (
          <button 
            onClick={handleGoToPaymentMethod} 
            className='w-full py-3 px-4 rounded-lg bg-[#013847] text-white text-[16px] font-raleway font-semibold hover:bg-[#013847]/90 transition-colors'
          >
            Go to Payment Method
          </button>
        ) : (
          <button 
            onClick={handleUploadDocumentation} 
            className='w-full py-3 px-4 rounded-lg bg-[#013847] text-white text-[16px] font-raleway font-semibold hover:bg-[#013847]/90 transition-colors'
          >
            {(requirementStatus?.is_required && requirementStatus?.presigned_url) ? 'Change' : 'Upload'}
          </button>
        )}
      </div>
    </div>
  )
} 



type ResourceScheduleGridProps = {
  availableDates: TrainingSchema[]
}

const ResourceScheduleGrid: React.FC<ResourceScheduleGridProps> = ({availableDates}) => {
  return (availableDates && availableDates?.length > 0) ? (
    <div className={`flex flex-col my-4 font-open-sans w-full h-auto`}>
      <div className='flex flex-row text-black rounded-lg h-auto'>
        {
          availableDates.map((date, i) => (
            <div key={i} className='mr-4'>
              <ResourceScheduleCard availableDate={date} />
            </div>
          ))
        }
      </div>
    </div>
  ) : (
    <div className={`flex flex-col text-[14px] text-[#aaa] my-4 w-full h-auto font-raleway`}>No schedule to show</div>
  )
} 


type ResourceScheduleCardProps = {
  availableDate: TrainingSchema
}

const ResourceScheduleCard: React.FC<ResourceScheduleCardProps> = ({availableDate}) => {

  if (availableDate?.times) {
    availableDate.times.sort((a, b) => {
      const timeA = moment(a, 'HH:mm');
      const timeB = moment(b, 'HH:mm');
      return timeA.diff(timeB);
    });
  }

  const availableDay = moment(availableDate.date).format('ddd MMM DD, YYYY')
  const availableTimes = availableDate?.times

  const isAvailableDayAfterToday = (availableDay: string): boolean => {
    const today = moment().startOf('day');
    const day = moment(availableDay).startOf('day');
    return day.isAfter(today);
  };

  const isPast = !isAvailableDayAfterToday(availableDay);

  return (
    <div className={`flex flex-row h-full rounded-lg border-[1px]  p-2 ${isPast ? 'bg-red-50 border-red-100' : 'border-[#E9E9E9]'}`}>
      <div className='my-auto m-2 text-primaryDark-300 h-1/2'>
        <BookingsIcon style={{height: 30}} />
      </div>
      <div className='flex flex-col my-auto m-2 h-full'>
          <p className={`text-[16px] font-semibold font-raleway ${isPast ? 'text-red-900' : ''}`}>{availableDay}</p>
          {
            availableTimes.map((time, i) => (
                <p key={i} className={`text-[16px] font-raleway ${isPast ? 'text-red-300' : 'text-[#808080]'}`}>{moment(time, 'HH:mm').format('hh:mm a')}</p>
              )
            )
          }
      </div>
    </div>
  )
} 



type OperatingHoursGridProps = {
  currentResourceOptions: ResourceOptionType
}

const OperatingHoursGrid: React.FC<OperatingHoursGridProps> = ({currentResourceOptions}) => {
  const formatPeriodLabel = (period?: TimeSlot['period_type']) => {
    if (!period) return '';
    return period.replace('_', ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const renderSlots = (slotsInput?: TimeSlot[] | any) => {
    const slots = Array.isArray(slotsInput) ? slotsInput : [];
    if (slots.length === 0) {
      return (
        <div className="inline-flex items-center px-2.5 py-1 rounded-md text-[13px] bg-slate-50 border border-slate-200 text-slate-500">
          Closed
        </div>
      );
    }

    return (
      <div className="flex flex-wrap gap-2">
        {slots.map((s: TimeSlot, idx: number) => {
          const styles = getPeriodStyles(s.period_type);
          return (
            <span key={idx} className={`inline-flex items-center px-2.5 py-1 rounded-md text-[13px] border ${styles.bg} ${styles.border}`}>
              <span className={`w-2 h-2 rounded-full mr-1.5 ${styles.dot}`}></span>
              <span className={`font-semibold ${styles.text}`}>{formatPeriodLabel(s.period_type)}</span>
              <span className="mx-2 text-slate-400">•</span>
              <span className="text-slate-800">{s.open_time} - {s.close_time}</span>
            </span>
          );
        })}
      </div>
    );
  };

  return (Object.keys(currentResourceOptions).length > 1) ? (
    <div className={`flex flex-col my-4 font-raleway w-full h-auto`}>
      <div className='text-[#000] text-[16px] rounded-lg h-auto'>
        <div className="grid grid-cols-[90px_minmax(0,1fr)] gap-y-3 font-open-sans">
          {/* Sun */}
          <div className="text-md font-bold pr-4 flex items-start pt-1">Sun</div>
          <div className="text-md">{renderSlots(currentResourceOptions?.sun as TimeSlot[])}</div>

          {/* Mon */}
          <div className="text-md font-bold pr-4 flex items-start pt-1">Mon</div>
          <div className="text-md">{renderSlots(currentResourceOptions?.mon as TimeSlot[])}</div>

          {/* Tue */}
          <div className="text-md font-bold pr-4 flex items-start pt-1">Tue</div>
          <div className="text-md">{renderSlots(currentResourceOptions?.tue as TimeSlot[])}</div>

          {/* Wed */}
          <div className="text-md font-bold pr-4 flex items-start pt-1">Wed</div>
          <div className="text-md">{renderSlots(currentResourceOptions?.wed as TimeSlot[])}</div>

          {/* Thu */}
          <div className="text-md font-bold pr-4 flex items-start pt-1">Thu</div>
          <div className="text-md">{renderSlots(currentResourceOptions?.thu as TimeSlot[])}</div>

          {/* Fri */}
          <div className="text-md font-bold pr-4 flex items-start pt-1">Fri</div>
          <div className="text-md">{renderSlots(currentResourceOptions?.fri as TimeSlot[])}</div>

          {/* Sat */}
          <div className="text-md font-bold pr-4 flex items-start pt-1">Sat</div>
          <div className="text-md">{renderSlots(currentResourceOptions?.sat as TimeSlot[])}</div>
        </div>
      </div>
    </div>
  ) : (
    <div className={`flex flex-col text-[14px] text-[#aaa] my-4 w-full h-auto font-raleway`}>No hours to show</div>
  )
} 




type ReviewsGridProps = {
  reviews: any[]
}

const ReviewsGrid: React.FC<ReviewsGridProps> = ({reviews}) => {

  const [currentFilter, setCurrentFilter] = useState<string>("All")

  return (reviews && reviews?.length > 0) ? (
    <div className={`flex flex-col my-4 font-open-sans w-full h-auto`}>
      <div className='mb-4 font-raleway text-[14px] font-semibold'>
        {
          ["All"].map((filter, i) => (
            <span key={i} onClick={()=>setCurrentFilter(filter)} className={`mr-2 ${(filter === currentFilter) ? 'text-primaryDark-300' : 'text-[#808080] cursor-pointer hover:text-primaryDark-400'} `} >
              {filter} &nbsp;
            </span>
          ))
        }
      </div>
      <div className='flex flex-row text-black rounded-lg h-auto'>
        {
           reviews.map((review, i) => (
            <div key={i} className='w-1/3 mr-2'>
              {review}
            </div>
          ))
        }
      </div>
    </div>
  ) : (
    <div className={`flex flex-col text-[14px] text-[#aaa] my-4 w-full h-auto font-raleway`}>No review to show</div>
  )
} 